"use client";

import { useState, useEffect, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { useAuth } from "@/contexts/AuthContext";
import Input from "@/components/ui/Input";
import PasswordInput from "@/components/ui/PasswordInput";
import { EnvelopeIcon } from "@heroicons/react/24/outline";
import Button from "@/components/ui/Button";
import { useToast } from "@/contexts/ToastContext";
import TurnstileWidget from "@/components/ui/TurnstileWidget";
import GoogleAuthButton from "@/components/ui/GoogleAuthButton";
import { sanitizeInternalPath } from "@/lib/navigation/authRedirect";
import { resolvePostAuthDestination } from "@/lib/navigation/postAuthRedirect";

// Login content component that uses useSearchParams
function LoginContent() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string>();
  const router = useRouter();
  const { login, loginWithGoogle, isAuthenticated, user } = useAuth();
  const { showToast } = useToast();
  const searchParams = useSearchParams();
  const redirectTo = sanitizeInternalPath(searchParams.get("redirectTo"));
  const showVerifyMessage =
    searchParams.get("verify") === "true" || needsVerification;

  useEffect(() => {
    if (isAuthenticated && user) {
      router.push(resolvePostAuthDestination(user, redirectTo));
    }
  }, [isAuthenticated, user, redirectTo, router]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    if (!captchaToken) {
      showToast("Please complete the CAPTCHA verification", "error");
      setIsLoading(false);
      return;
    }

    try {
      const result = await login(email, password, captchaToken);

      if (result.success) {
        router.push(
          resolvePostAuthDestination(result.user ?? user, redirectTo),
        );
      } else {
        // Check if the error is related to email verification
        if (result.message.includes("Email not confirmed")) {
          setNeedsVerification(true);
          setVerificationEmail(email);
        }
      }
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    try {
      const result = await loginWithGoogle(redirectTo);
      if (!result.success) {
        showToast(result.message, "error");
      }
    } catch (error) {
      console.error("Google login error:", error);
      showToast("Failed to login with Google", "error");
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        {showVerifyMessage && (
          <div className="rounded-md bg-yellow-50 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <EnvelopeIcon
                  className="h-5 w-5 text-yellow-400"
                  aria-hidden="true"
                />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Email verification required
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    We&apos;ve sent an account verification email to your inbox.
                    Please use the OTP to verify your account.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            Log in to your account
          </h1>
          <p className="mt-2 text-sm text-text-light">
            Or{" "}
            <Link
              href="/signup"
              className="font-medium text-primary hover:text-primary-600"
            >
              create a new account
            </Link>
          </p>
        </div>

        <div className="mt-8 space-y-6">
          <GoogleAuthButton
            onClick={handleGoogleLogin}
            isLoading={isGoogleLoading}
            mode="signin"
          />

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-surface text-text-light">
                Or continue with email
              </span>
            </div>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                label="Email address"
                value={email}
                onChange={(e) => {
                  const nextEmail = e.target.value;
                  setEmail(nextEmail);
                  if (needsVerification && nextEmail !== verificationEmail) {
                    setNeedsVerification(false);
                    setVerificationEmail("");
                  }
                }}
              />

              <PasswordInput
                id="password"
                name="password"
                autoComplete="current-password"
                required
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <label
                    htmlFor="remember-me"
                    className="ml-2 block text-sm text-foreground"
                  >
                    Remember me
                  </label>
                </div>

                <div className="text-sm">
                  <Link
                    href="/forgot-password"
                    className="font-medium text-primary hover:text-primary-600"
                  >
                    Forgot your password?
                  </Link>
                </div>
              </div>

              <TurnstileWidget onTokenChange={setCaptchaToken} />

              <Button
                type="submit"
                className="w-full"
                variant="primary"
                size="lg"
                isLoading={isLoading}
                disabled={!email || !password || !captchaToken || isLoading}
                haptic="light"
              >
                {isLoading ? "Logging in..." : "Log in"}
              </Button>

              {/* Show verification link when user needs email verification */}
              {needsVerification && verificationEmail && (
                <div className="rounded-md bg-blue-50 p-4 border border-blue-200">
                  <div className="flex items-center">
                    <EnvelopeIcon className="h-5 w-5 text-blue-400 mr-2" />
                    <div className="text-sm">
                      <p className="text-blue-800 font-medium">
                        Need to verify your email?
                      </p>
                      <p className="text-blue-700 mt-1">
                        <Link
                          href={`/verify-account?email=${encodeURIComponent(verificationEmail)}`}
                          className="font-medium text-blue-600 hover:text-blue-500 underline"
                        >
                          Click here to verify your account
                        </Link>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-sm text-center text-text-light">Or</p>

              <Button
                href="/signup"
                type="button"
                className="w-full"
                variant="secondary"
                size="lg"
              >
                Create account
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Loading fallback
function LoginLoading() {
  return (
    <div className="min-h-[80vh] flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-4 text-lg">Loading...</p>
      </div>
    </div>
  );
}

// Main component with Suspense boundary
export default function LoginPage() {
  return (
    <Suspense fallback={<LoginLoading />}>
      <LoginContent />
    </Suspense>
  );
}
